import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import i18n from 'i18next';
import { history } from 'index';
import { openWindow } from 'utils/login';
import { generateLoginUrl } from 'utils/login';
import { setWarning, setFailed } from 'store/module/notify';
import { setOtpVerified } from 'store/module/firebase';
import { signout } from 'features/login/utils/firebaseManager';
import { toOTPVerifyPage } from 'features/login/hooks/useInitAndRedirectApp';
import { sentryCaptureException } from 'utils/sentry';
import {
    getGeneralErrCode,
    isFetchNetworkError,
    isTooManyAttemptErr,
    isUnauthorizedToGetAccessToken,
    isOtpValidationFailedError,
    isPermissionDeniedError,
    is404NotFoundError,
    isSponsorNotApprovedError
} from 'utils/errorObjectChecker';
import { EXCEPTION_FAILED } from 'config/errorTypes';

export default function useApiFailedHandler() {
    const dispatch = useDispatch();
    return useCallback(
        async (errObj, defaultErrMsg = i18n.t('message_save_error'), errorType, body) => {
            console.error(errObj);
            // 有 message 代表是透過 generateApiErrObj 產生
            const error = errObj.message ? errObj : generateApiErrObj(errObj, errorType, body);
            const generalErrCode = getGeneralErrCode(error);

            if (generalErrCode)
                return dispatch(setFailed(i18n.t('notify.busy_error', { code: generalErrCode })));

            switch (true) {
                case isOtpValidationFailedError(error):
                    dispatch(setOtpVerified(false));
                    toOTPVerifyPage(history);
                    break;
                case isPermissionDeniedError(error):
                    dispatch(setFailed(i18n.t('permission_denied.title')));
                    break;
                case isUnauthorizedToGetAccessToken(error):
                    dispatch(setWarning(i18n.t('notify.login_session_expired')));
                    await signout().then(() => {
                        const refUrl = `${window.location.origin}/utils/loginhandler`;
                        openWindow(generateLoginUrl(refUrl), { name: 'editor-login' });
                    });
                    break;
                case isFetchNetworkError(error):
                    dispatch(setFailed(i18n.t('notify.network_error')));
                    break;
                case isTooManyAttemptErr(error):
                    dispatch(setFailed(i18n.t('notify.too_many_attempt_error')));
                    break;
                case is404NotFoundError(error):
                    const errorMessage = defaultErrMsg || i18n.t('notify.record_not_found');
                    dispatch(setFailed(errorMessage));
                    break;
                case isSponsorNotApprovedError(error):
                    // 主辦單位尚未審核通過為正常業務狀態，顯示提示但不回報 Sentry
                    dispatch(setFailed(i18n.t('notify.sponsor_not_approved')));
                    break;
                default: {
                    const eventId = sentryCaptureException(error);
                    console.error('Event Id:', eventId);
                    dispatch(setFailed(`${i18n.t(defaultErrMsg)}`));
                }
            }
        },
        [dispatch]
    );
}

export function generateApiErrObj(apiErr = {}, type = EXCEPTION_FAILED, body) {
    let errObj = new Error(type);
    apiErr.errType = type;
    errObj.extraInfo = apiErr;

    if (!Array.isArray(apiErr) && typeof apiErr === 'object' && Boolean(body)) {
        errObj.extraInfo.body = body;
    }

    return errObj;
}
